import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import CustomTable from '../../components/CustomTable/CustomTable';
import CustomModal from '../../components/CustomModal';
import Title from '../../components/Title';
import Pagination from '../../components/Pagination';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Space } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import Spacer from '../../components/Spacer/Spacer';
import { useNavigate } from 'react-router-dom';
import {
    allFaqs,
    deleteFaq,
    faqsLoadingStatus,
    faqsMeta,
    getFaqs,
} from '../../redux/slices/faqSlice';
import { toast } from 'react-toastify';
import moment from 'moment';

export default function FAQPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const faqs = useSelector(allFaqs);
    const faqsStatus = useSelector(faqsLoadingStatus);
    const faqsMetainformation = useSelector(faqsMeta);

    const [isDeleteQuestionModalVisible, setIsDeleteQuestionModalVisible] =
        useState(false);
    const [questionData, setQuestionData] = useState({});
    const [filter, setFilter] = useState('');
    const [errors, setErrors] = useState({});

    useEffect(() => {
        dispatch(getFaqs({ name: '' }));
    }, [dispatch]);

    const onCreateQuestionClick = () => {
        navigate('create');
    };

    const onEditQuestion = (record, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        navigate(`${record.id}/edit`);
    };

    const onGroupsManageClick = () => {
        navigate('groups');
    };

    const getFilteredQuestions = (value = '') => {
        const data = { name: value };
        setFilter(value);
        dispatch(getFaqs(data));
    };

    const toggleDeleteQuestionModal = (record, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (!record) {
            setIsDeleteQuestionModalVisible(!isDeleteQuestionModalVisible);
            setErrors({});
        } else {
            setQuestionData({ name: record.title, id: record.id });
            setIsDeleteQuestionModalVisible(!isDeleteQuestionModalVisible);
            setErrors({});
        }
    };

    const onDeleteFaq = async () => {
        try {
            await dispatch(deleteFaq(questionData.id)).unwrap();
            toast.success('Package deleted successfully');
            toggleDeleteQuestionModal();
            dispatch(getFaqs({ name: '' }));
        } catch (error) {
            toast.error(error.errors);
            setErrors(error.errors);
        }
    };

    const getFaqsPage = (page) => {
        dispatch(
            getFaqs({
                name: filter,
                page,
            }),
        );
    };

    const paginationControlls = {
        previousClick: getFaqsPage,
        nextClick: getFaqsPage,
        pageChange: getFaqsPage,
    };

    const actionsOption = {
        title: 'Actions',
        render: (_, record) => (
            <Space size="middle">
                <EditOutlined onClick={(e) => onEditQuestion(record, e)} />
                <DeleteOutlined
                    onClick={(e) => toggleDeleteQuestionModal(record, e)}
                />
            </Space>
        ),
    };

    const formattedFaqs = useMemo(
        () =>
            faqs?.map((el) => {
                const foundGroupNames =
                    el?.group?.names?.find(
                        (el) => el.lang_code === 'en' && !!el.name,
                    ) || el?.group?.names?.find((el) => !!el.name);

                const foundFaqNames =
                    el?.details?.find(
                        (el) => el.lang_code === 'EN' && !!el.title,
                    ) || el?.details?.find((el) => !!el.title);

                return {
                    ...el,
                    title: foundFaqNames?.title || null,
                    group: el?.group ? foundGroupNames?.name : null,
                    created_at: moment(el.created_at).format('YYYY-MM-DD'),
                };
            }),
        [faqs],
    );

    return (
        <div>
            <Helmet>
                <title>Console Defentry - FAQ</title>
            </Helmet>
            <Title>FAQ</Title>
            <div className="table-wrapper">
                <CustomTable
                    tableHeader={['ID', 'FAQ', 'Group', 'Created', 'Actions']}
                    columnsDataMap={{
                        Created: 'created_at',
                        FAQ: 'title',
                    }}
                    functionalCell={{ Actions: actionsOption }}
                    onAddButtonClick={onCreateQuestionClick}
                    tablePannelTitle="FAQ"
                    searchInputPlaceholder="Search by title"
                    topPannelButtonText="Add question"
                    data={formattedFaqs}
                    isLoading={faqsStatus === 'loading'}
                    withFilter
                    asyncFilter={getFilteredQuestions}
                    additionalButtons={
                        <>
                            <Button
                                className={`details-table__button modal__button-cancel`}
                                type="secondary"
                                size="large"
                                style={{ width: 'auto' }}
                                onClick={onGroupsManageClick}
                            >
                                Add/order groups
                            </Button>
                            <Spacer orientation="vertical" size={6} />
                        </>
                    }
                />
                {faqsStatus === 'succeeded' && (
                    <Pagination
                        data={faqsMetainformation}
                        controlls={paginationControlls}
                    />
                )}
            </div>
            <CustomModal
                title={
                    <p>
                        Are you sure you want to delete
                        <span className="highlighted-text">{` ${questionData.name} `}</span>
                        question?
                    </p>
                }
                subText="You will delete this question without recovery"
                subTextAlign="center"
                isModalVisible={isDeleteQuestionModalVisible}
                onAcceptButtonClick={onDeleteFaq}
                onCancelButtonClick={toggleDeleteQuestionModal}
                cancelButtonText="Cancel"
                acceptButtonText="Delete"
            >
                {errors.package && (
                    <p className="modal__error-text">{errors.package}</p>
                )}
            </CustomModal>
        </div>
    );
}
